import { useCallback, useEffect, useRef, useState } from 'react'
import { io, type Socket } from 'socket.io-client'
import type { z } from 'zod'

import {
  roomClosedSchema,
  roomCreatedSchema,
  roomErrorSchema,
  roomJoinedSchema,
  roomMilestoneSchema,
  roomStateSchema,
} from '@hazel/shared'

import type { ConnectionStatus, RoomState } from './types.js'

/**
 * Reads an inbound message, or drops it.
 *
 * The server parses everything a client sends. This is the same thing pointing
 * the other way, and it is not hypothetical: the room payload changed shape
 * once and the client's type carried on claiming the old one, which nothing
 * checked. Both sides' tests passed and the seam between them was broken.
 *
 * A payload that does not parse is logged and dropped rather than rendered. A
 * television showing stale content is bad; one rendering a half-understood
 * payload is worse.
 */
function read<Schema extends z.ZodType>(
  schema: Schema,
  raw: unknown,
  event: string,
): z.infer<Schema> | null {
  const parsed = schema.safeParse(raw)
  if (parsed.success) return parsed.data

  // There is no logger in the browser, and a payload the client cannot
  // understand is exactly the thing somebody debugging a living room full of
  // phones needs to be able to see.
  console.error(`Ignored a ${event} the server sent that this client cannot read`, parsed.error)

  return null
}

export type RoomEmit = (event: string, payload?: unknown) => void

/** What the server hands back when a television opens or reclaims a room. */
export interface OpenedRoom {
  code: string
  hostToken: string
}

/** What the server hands back when a phone claims or reclaims a seat. */
export interface SeatClaim {
  seatId: string
  /** This phone's alone. It must never reach the shared board. */
  token: string
  rejoined: boolean
}

export interface UseRoomSocketOptions {
  /**
   * Runs on every connect, including reconnects. This is where a host
   * reclaims its room and a player reclaims their seat.
   */
  onConnect?: (emit: RoomEmit) => void
  /** Fired when a room is opened or successfully reclaimed. */
  onRoomOpened?: (room: OpenedRoom) => void
  /** Fired when this client is seated, or reseated after a drop. */
  onSeated?: (seat: SeatClaim) => void
  /**
   * Fired when this phone's player passes a milestone.
   *
   * Sent to one socket, never to the room — see the server's
   * SERVER_EVENTS.milestone. The television holds no seat and so never hears
   * it, which is the point: a child congratulated on a big screen while three
   * others watch is a different feeling from the one intended.
   */
  onMilestone?: (games: number) => void
  /**
   * Fired when the server closes the room. Nothing more will arrive for it.
   * Given `emit` for the same reason onConnect is: so a caller can react
   * without needing to hold the socket itself.
   */
  onClosed?: (emit: RoomEmit) => void
}

export interface RoomSocket {
  /** Sends to the server. Safe to call before the socket is up; it is a no-op. */
  readonly emit: RoomEmit
  readonly status: ConnectionStatus
  readonly room: RoomState | null
  readonly error: string | null
  readonly clearError: () => void
}

/**
 * One socket for the lifetime of the component.
 *
 * Socket.IO reconnects by itself, so the job here is to report honestly what
 * is happening rather than to re-implement it. A living room with patchy WiFi
 * should show "reconnecting" rather than a board frozen on whatever it last
 * heard, which looks identical to the game having crashed.
 *
 * The socket is exposed as an `emit` function rather than as the instance.
 * Callers only ever need to send, and keeping the instance out of render means
 * the ref holding it is only ever touched inside effects and event handlers.
 *
 * Callbacks are held in refs, updated in an effect, so a caller passing an
 * inline function cannot tear the socket down and rebuild it on every render.
 * Correctness should not depend on remembering to wrap things in useCallback.
 */
export function useRoomSocket(options: UseRoomSocketOptions = {}): RoomSocket {
  const socketRef = useRef<Socket | null>(null)
  const optionsRef = useRef(options)

  const [status, setStatus] = useState<ConnectionStatus>('connecting')
  const [room, setRoom] = useState<RoomState | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    optionsRef.current = options
  })

  const emit = useCallback<RoomEmit>((event, payload) => {
    socketRef.current?.emit(event, payload)
  }, [])

  useEffect(() => {
    const connection = io({ transports: ['websocket', 'polling'] })
    socketRef.current = connection

    connection.on('connect', () => {
      setStatus('connected')
      optionsRef.current.onConnect?.(emit)
    })
    connection.on('disconnect', () => setStatus('reconnecting'))
    connection.io.on('reconnect_attempt', () => setStatus('reconnecting'))

    connection.on('room:created', (raw: unknown) => {
      const opened = read(roomCreatedSchema, raw, 'room:created')
      if (opened) optionsRef.current.onRoomOpened?.(opened)
    })

    connection.on('room:joined', (raw: unknown) => {
      const seat = read(roomJoinedSchema, raw, 'room:joined')
      if (seat) optionsRef.current.onSeated?.(seat)
    })

    connection.on('room:closed', (raw: unknown) => {
      if (!read(roomClosedSchema, raw, 'room:closed')) return

      // Told rather than left to notice. A screen that simply stops updating
      // looks identical to one that is working but quiet.
      setRoom(null)
      optionsRef.current.onClosed?.(emit)
    })

    connection.on('room:state', (raw: unknown) => {
      const next = read(roomStateSchema, raw, 'room:state')
      if (!next) return

      setRoom(next)
      // A refusal overtaken by events should stop being shown — a transient
      // "nobody has joined yet" must not outlive somebody joining.
      setError(null)
    })

    connection.on('room:milestone', (raw: unknown) => {
      const award = read(roomMilestoneSchema, raw, 'room:milestone')
      if (award) optionsRef.current.onMilestone?.(award.milestone)
    })

    connection.on('room:error', (raw: unknown) => {
      const refusal = read(roomErrorSchema, raw, 'room:error')
      if (refusal) setError(refusal.reason)
    })

    return () => {
      connection.removeAllListeners()
      connection.disconnect()
      socketRef.current = null
    }
  }, [emit])

  const clearError = useCallback(() => setError(null), [])

  return { emit, status, room, error, clearError }
}
